import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import type { NostrEvent } from '@nostrify/nostrify';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNostr } from '@nostrify/react';
import {
  Check,
  Copy,
  CornerDownRight,
  Heart,
  MessageCircle,
  Repeat2,
  Send,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { timeAgo } from '@/lib/timeAgo';
import { getDisplayName } from '@/lib/getDisplayName';
import { sanitizeUrl } from '@/lib/sanitizeUrl';
import { ownerPubkey } from '@/lib/owner';
import { useAuthor } from '@/hooks/useAuthor';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useNoteStats } from '@/hooks/useNoteStats';
import { useToast } from '@/hooks/useToast';
import { NoteContent } from '@/components/NoteContent';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';

/** Kind-6 reposts embed the boosted event as JSON in their content. */
function parseRepost(event: NostrEvent): NostrEvent | null {
  if (event.kind !== 6 || !event.content) return null;
  try {
    const inner = JSON.parse(event.content) as NostrEvent;
    if (
      inner &&
      typeof inner.id === 'string' &&
      typeof inner.pubkey === 'string' &&
      typeof inner.content === 'string'
    ) {
      return inner;
    }
  } catch {
    // content wasn't JSON — some clients leave it empty
  }
  return null;
}

/** Direct parent of a reply: the `e` tag marked "reply", else the last one (NIP-10). */
function getParentId(event: NostrEvent): string | undefined {
  const eTags = event.tags.filter(([name]) => name === 'e');
  if (eTags.length === 0) return undefined;
  const marked = eTags.find(([, , , marker]) => marker === 'reply');
  return (marked ?? eTags.at(-1))?.[1];
}

function AuthorChip({ pubkey }: { pubkey: string }) {
  const author = useAuthor(pubkey);
  const metadata = author.data?.metadata;
  const name = getDisplayName(metadata, pubkey);
  const picture = sanitizeUrl(metadata?.picture);

  return (
    <Link
      to={`/${nip19.npubEncode(pubkey)}`}
      className="group flex w-fit min-w-0 items-center gap-2"
    >
      <Avatar size="sm">
        <AvatarImage src={picture} alt={name} />
        <AvatarFallback className="text-[10px] uppercase">{name.slice(0, 2)}</AvatarFallback>
      </Avatar>
      <span className="truncate text-sm font-medium transition-colors group-hover:text-primary">
        {name}
      </span>
    </Link>
  );
}

function ReplyContext({ parentId }: { parentId: string }) {
  const { nostr } = useNostr();
  const { data: parent } = useQuery({
    queryKey: ['note', parentId],
    queryFn: async ({ signal }) => {
      const [event] = await nostr.query([{ ids: [parentId] }], {
        signal: AbortSignal.any([signal, AbortSignal.timeout(4000)]),
      });
      return event ?? null;
    },
    staleTime: 10 * 60 * 1000,
    retry: 1,
  });
  const author = useAuthor(parent?.pubkey);
  const name = parent ? getDisplayName(author.data?.metadata, parent.pubkey) : 'a note';

  return (
    <Link
      to={`/${nip19.noteEncode(parentId)}`}
      className="mb-3 flex w-fit items-center gap-1.5 font-mono text-xs text-muted-foreground transition-colors hover:text-primary"
    >
      <CornerDownRight className="size-3.5 shrink-0 text-primary" />
      replying to {name}
    </Link>
  );
}

function BoostFallback({ event }: { event: NostrEvent }) {
  const eId = event.tags.find(([name]) => name === 'e')?.[1];
  if (!eId) {
    return <p className="text-sm text-muted-foreground">boosted a note</p>;
  }
  return (
    <Link
      to={`/${nip19.noteEncode(eId)}`}
      className="block rounded-lg border bg-muted/30 p-4 text-sm text-muted-foreground transition-colors hover:border-primary/40"
    >
      boosted a note —{' '}
      <span className="font-mono text-xs">{nip19.noteEncode(eId).slice(0, 24)}…</span>
    </Link>
  );
}

interface ActionButtonProps {
  icon: typeof Heart;
  count: number;
  label: string;
  active?: boolean;
  activeClass?: string;
  disabled?: boolean;
  onClick: () => void;
}

function ActionButton({
  icon: Icon,
  count,
  label,
  active,
  activeClass = 'text-primary',
  disabled,
  onClick,
}: ActionButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className={cn(
        'flex items-center gap-1.5 rounded-full px-2.5 py-1.5 text-xs text-muted-foreground transition-colors',
        'hover:bg-primary/10 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        'disabled:opacity-50',
        active && activeClass,
      )}
    >
      <Icon className={cn('size-4', active && label === 'Like' && 'fill-current')} />
      {count > 0 && <span className="tabular-nums">{count}</span>}
    </button>
  );
}

export function NoteCard({ event, className }: { event: NostrEvent; className?: string }) {
  const { user } = useCurrentUser();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { mutateAsync: publish, isPending: isPublishing } = useNostrPublish();
  const { data: stats } = useNoteStats(event.id);

  const [replyOpen, setReplyOpen] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [copied, setCopied] = useState(false);

  const repostOf = useMemo(() => parseRepost(event), [event]);
  const parentId = useMemo(() => (event.kind === 1 ? getParentId(event) : undefined), [event]);
  const noteId = useMemo(() => nip19.noteEncode(event.id), [event.id]);

  const refreshStats = () => queryClient.invalidateQueries({ queryKey: ['note-stats', event.id] });

  const requireLogin = () => {
    toast({
      title: 'Log in to interact',
      description: 'Hit "Join" in the top right to like, reply, and boost.',
    });
  };

  const handleLike = async () => {
    if (!user) return requireLogin();
    if (stats?.likedByMe || isPublishing) return;
    try {
      await publish({
        kind: 7,
        content: '+',
        tags: [
          ['e', event.id],
          ['p', event.pubkey],
          ['k', String(event.kind)],
        ],
      });
      refreshStats();
    } catch {
      toast({
        title: 'Reaction failed',
        description: 'Your signer or relays rejected the event.',
        variant: 'destructive',
      });
    }
  };

  const handleRepost = async () => {
    if (!user) return requireLogin();
    if (stats?.repostedByMe || isPublishing) return;
    try {
      await publish({
        kind: 6,
        content: JSON.stringify(event),
        tags: [
          ['e', event.id],
          ['p', event.pubkey],
        ],
      });
      toast({ title: 'Boosted', description: 'Shared to your relays.' });
      refreshStats();
    } catch {
      toast({
        title: 'Boost failed',
        description: 'Your signer or relays rejected the event.',
        variant: 'destructive',
      });
    }
  };

  const handleReply = async () => {
    if (!user) return requireLogin();
    const content = replyText.trim();
    if (!content || isPublishing) return;

    const rootTag = event.tags.find(
      ([name, , , marker]) => name === 'e' && marker === 'root',
    );
    const tags: string[][] = [
      ...(rootTag ? [[rootTag[0], rootTag[1], rootTag[2] ?? '', 'root']] : []),
      ['e', event.id, '', rootTag ? 'reply' : 'root'],
      ['p', event.pubkey],
    ];

    try {
      await publish({ kind: 1, content, tags });
      setReplyText('');
      setReplyOpen(false);
      toast({ title: 'Reply sent' });
      refreshStats();
    } catch {
      toast({
        title: 'Reply failed',
        description: 'Your signer or relays rejected the event.',
        variant: 'destructive',
      });
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(`nostr:${noteId}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast({ title: 'Could not copy to clipboard', variant: 'destructive' });
    }
  };

  return (
    <article
      className={cn(
        'rounded-xl border bg-card p-5 transition-colors hover:border-primary/30',
        className,
      )}
    >
      {event.kind === 6 && (
        <p className="mb-3 flex items-center gap-2 font-mono text-xs text-muted-foreground">
          <Repeat2 className="size-3.5 text-primary" />
          boosted
        </p>
      )}
      {parentId && <ReplyContext parentId={parentId} />}

      {event.kind === 6 ? (
        repostOf ? (
          <div className="rounded-lg border bg-muted/30 p-4">
            <div className="mb-2.5">
              <AuthorChip pubkey={repostOf.pubkey} />
            </div>
            <NoteContent
              event={repostOf}
              className="text-sm leading-relaxed"
              disableNoteEmbeds
            />
          </div>
        ) : (
          <BoostFallback event={event} />
        )
      ) : (
        <>
          {event.pubkey !== ownerPubkey && (
            <div className="mb-3">
              <AuthorChip pubkey={event.pubkey} />
            </div>
          )}
          <NoteContent event={event} className="text-[15px] leading-relaxed" />
        </>
      )}

      <div className="mt-4 flex items-center justify-between gap-2 border-t pt-3">
        <Link
          to={`/${noteId}`}
          className="shrink-0 font-mono text-xs text-muted-foreground transition-colors hover:text-primary"
        >
          {timeAgo(event.created_at)}
        </Link>

        <div className="flex items-center gap-0.5">
          <ActionButton
            icon={MessageCircle}
            count={stats?.replies ?? 0}
            label="Reply"
            onClick={() => (user ? setReplyOpen((open) => !open) : requireLogin())}
          />
          <ActionButton
            icon={Repeat2}
            count={stats?.reposts ?? 0}
            label="Boost"
            active={stats?.repostedByMe}
            activeClass="text-green-500"
            disabled={isPublishing}
            onClick={handleRepost}
          />
          <ActionButton
            icon={Heart}
            count={stats?.likes ?? 0}
            label="Like"
            active={stats?.likedByMe}
            disabled={isPublishing}
            onClick={handleLike}
          />
          <button
            type="button"
            onClick={handleCopy}
            aria-label="Copy note link"
            className="flex items-center rounded-full p-1.5 text-muted-foreground transition-colors hover:bg-primary/10 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {copied ? <Check className="size-4 text-green-500" /> : <Copy className="size-4" />}
          </button>
        </div>
      </div>

      {replyOpen && user && (
        <div className="mt-3 space-y-2 border-t pt-3">
          <Textarea
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder="Write your reply…"
            rows={3}
            className="resize-none"
            autoFocus
          />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setReplyOpen(false)}>
              Cancel
            </Button>
            <Button size="sm" onClick={handleReply} disabled={!replyText.trim() || isPublishing}>
              <Send className="size-3.5" />
              {isPublishing ? 'Sending…' : 'Reply'}
            </Button>
          </div>
        </div>
      )}
    </article>
  );
}
